<template>
  <div class="menu-data-list-container">
    <el-card shadow="never" class="menu-data-card">
      <div slot="header" class="card-header">
        <span>菜单数据列表</span>
        <div class="card-actions">
          <el-button size="small" @click="refreshCurrentView">刷新</el-button>
        </div>
      </div>

      <div class="menu-data-layout">
        <div class="menu-tree-panel">
          <div class="panel-title">菜单树</div>
          <el-input
            v-model.trim="treeKeyword"
            size="small"
            clearable
            placeholder="筛选菜单节点"
            class="tree-search"
          />
          <el-tree
            ref="menuTree"
            :data="treeData"
            :props="treeProps"
            node-key="id"
            highlight-current
            default-expand-all
            :expand-on-click-node="false"
            :filter-node-method="filterTreeNode"
            v-loading="treeLoading"
            class="menu-tree"
            @node-click="handleNodeClick"
          >
            <span slot-scope="{ data }" class="tree-node">
              <span class="tree-node__title">{{ data.title }}</span>
              <span class="tree-node__path">{{ data.path }}</span>
            </span>
          </el-tree>
        </div>

        <div class="menu-detail-panel">
          <div v-if="selectedNodeDetail" class="selected-summary">
            <div class="summary-main">
              <div class="summary-title">{{ selectedNodeDetail.title }}</div>
              <div class="summary-meta">
                <span>路由：{{ selectedNodeDetail.path }}</span>
                <span>层级：{{ levelText(selectedNodeDetail.level) }}</span>
                <span>子节点：{{ selectedNodeDetail.directChildCount }}</span>
              </div>
            </div>
            <div class="summary-actions">
              <el-button
                v-if="selectedNodeDetail.id !== allNodeId"
                size="mini"
                type="primary"
                @click="openEditDialog(selectedNodeDetail)"
              >
                编辑当前节点
              </el-button>
              <el-button
                v-if="selectedNodeDetail.id !== allNodeId"
                size="mini"
                type="danger"
                @click="handleDelete(selectedNodeDetail)"
              >
                删除当前节点
              </el-button>
            </div>
          </div>

          <div class="child-toolbar">
            <div class="panel-title">子节点列表</div>
            <el-input
              v-model.trim="keyword"
              size="small"
              clearable
              placeholder="按名称或路由筛选子节点"
              class="child-search"
            />
          </div>

          <el-table
            :data="pagedChildMenus"
            v-loading="childLoading"
            border
            style="width: 100%"
          >
            <el-table-column
              type="index"
              label="序号"
              width="70"
              align="center"
              header-align="center"
              :index="indexMethod"
            />
            <el-table-column prop="title" label="菜单名称" min-width="180" />
            <el-table-column prop="path" label="路由地址" min-width="220" />
            <el-table-column prop="componentPath" label="组件路径" min-width="220" />
            <el-table-column prop="sort" label="排序" width="90" align="center" />
            <el-table-column label="子节点数" width="100" align="center">
              <template slot-scope="{ row }">
                {{ row.directChildCount }}
              </template>
            </el-table-column>
            <el-table-column label="操作" width="180" align="center" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="primary" size="mini" @click="openEditDialog(row)">
                  编辑
                </el-button>
                <el-button type="danger" size="mini" @click="handleDelete(row)">
                  删除
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <el-empty
            v-if="!childLoading && !filteredChildMenus.length"
            description="当前节点下没有子节点"
            :image-size="88"
          />

          <el-pagination
            style="margin-top: 20px; text-align: right"
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="currentPage"
            :page-size="pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="filteredChildMenus.length"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
          />
        </div>
      </div>
    </el-card>

    <el-dialog
      title="编辑菜单"
      :visible.sync="editDialogVisible"
      width="560px"
      @closed="resetEditForm"
    >
      <el-form
        ref="editForm"
        :model="editForm"
        :rules="editRules"
        label-width="100px"
      >
        <el-form-item label="菜单名称" prop="title">
          <el-input v-model.trim="editForm.title" maxlength="20" show-word-limit />
        </el-form-item>
        <el-form-item label="路由地址" prop="path">
          <el-input v-model.trim="editForm.path" placeholder="/data/menu-list" />
        </el-form-item>
        <el-form-item label="组件路径" prop="componentPath">
          <el-input
            v-model.trim="editForm.componentPath"
            placeholder="data/menu-list/index.vue"
          />
        </el-form-item>
        <el-form-item label="排序" prop="sort">
          <el-input-number
            v-model="editForm.sort"
            :min="0"
            :max="9999"
            controls-position="right"
          />
        </el-form-item>
        <el-form-item v-if="editForm.isRoot" label="图标" prop="icon">
          <el-input v-model.trim="editForm.icon" placeholder="el-icon-menu" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitEdit">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { initDynamicRoutes } from '@/router'
import { fetchMenuTree, fetchFlatMenuList, updateMenu, deleteMenu } from '@/api/modules/menu'

const ALL_NODE_ID = '__all__'

function countDescendants(node) {
  const children = Array.isArray(node.children) ? node.children : []
  return children.reduce((total, child) => total + 1 + countDescendants(child), 0)
}

function flattenMenuTree(tree, level = 1, parent = null, result = []) {
  tree.forEach(node => {
    const children = Array.isArray(node.children) ? node.children : []

    result.push({
      ...node,
      level,
      parentTitle: parent ? parent.title : '-',
      directChildCount: children.length,
      childCount: countDescendants(node)
    })

    if (children.length) {
      flattenMenuTree(children, level + 1, node, result)
    }
  })

  return result
}

export default {
  name: 'MenuDataList',
  data() {
    return {
      treeLoading: false,
      childLoading: false,
      saving: false,
      allNodeId: ALL_NODE_ID,
      treeKeyword: '',
      keyword: '',
      currentPage: 1,
      pageSize: 10,
      treeData: [],
      rawTreeData: [],
      flatMenuList: [],
      childMenus: [],
      selectedNodeId: '',
      editDialogVisible: false,
      treeProps: {
        label: 'title',
        children: 'children'
      },
      editForm: this.createEditForm(),
      editRules: {
        title: [
          { required: true, message: '请输入菜单名称', trigger: 'blur' },
          { min: 2, max: 20, message: '长度应为 2 到 20 个字符', trigger: 'blur' }
        ],
        path: [
          { required: true, message: '请输入路由地址', trigger: 'blur' },
          { pattern: /^\/[A-Za-z0-9/_-]+$/, message: '路由地址格式不正确', trigger: 'blur' }
        ],
        componentPath: []
      }
    }
  },
  computed: {
    selectedNodeDetail() {
      if (this.selectedNodeId === ALL_NODE_ID) {
        return {
          id: ALL_NODE_ID,
          title: '全部',
          path: '/',
          level: 0,
          directChildCount: this.rawTreeData.length,
          childCount: this.flatMenuList.length
        }
      }

      return this.flatMenuList.find(item => item.id === this.selectedNodeId) || null
    },
    filteredChildMenus() {
      const keyword = this.keyword.trim().toLowerCase()
      if (!keyword) {
        return this.childMenus
      }

      return this.childMenus.filter(item => {
        return [item.title, item.path, item.componentPath]
          .filter(Boolean)
          .some(value => String(value).toLowerCase().includes(keyword))
      })
    },
    pagedChildMenus() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.filteredChildMenus.slice(start, end)
    }
  },
  watch: {
    treeKeyword(value) {
      if (this.$refs.menuTree) {
        this.$refs.menuTree.filter(value)
      }
    },
    keyword() {
      this.currentPage = 1
    }
  },
  created() {
    this.initializePage()
  },
  methods: {
    createEditForm() {
      return {
        id: '',
        title: '',
        path: '',
        componentPath: '',
        sort: 0,
        icon: '',
        isRoot: false
      }
    },
    levelText(level) {
      if (level === 0) {
        return '全部'
      }

      if (level === 1) {
        return '一级'
      }
      if (level === 2) {
        return '二级'
      }
      return '三级'
    },
    filterTreeNode(value, data) {
      if (!value) {
        return true
      }

      const keyword = value.toLowerCase()
      return [data.title, data.path]
        .filter(Boolean)
        .some(item => String(item).toLowerCase().includes(keyword))
    },
    findNodeById(tree, targetId) {
      for (const node of tree) {
        if (node.id === targetId) {
          return node
        }

        const children = Array.isArray(node.children) ? node.children : []
        if (children.length) {
          const result = this.findNodeById(children, targetId)
          if (result) {
            return result
          }
        }
      }

      return null
    },
    findDefaultNode(tree) {
      return tree[0] || null
    },
    decorateMenus(list) {
      return list.map(item => {
        const detail = this.flatMenuList.find(menu => menu.id === item.id)
        return {
          ...item,
          ...detail,
          componentPath: item.componentPath || (detail && detail.componentPath) || ''
        }
      })
    },
    async initializePage(selectedId) {
      this.treeLoading = true

      try {
        const treePayload = await fetchMenuTree()
        this.rawTreeData = treePayload || []
        this.treeData = [
          {
            id: ALL_NODE_ID,
            title: '全部',
            path: '/',
            children: this.rawTreeData
          }
        ]
        this.flatMenuList = flattenMenuTree(this.rawTreeData)

        const targetNode = this.findNodeById(this.treeData, selectedId) || this.findDefaultNode(this.treeData)
        if (!targetNode) {
          this.childMenus = []
          this.selectedNodeId = ''
          return
        }

        this.selectedNodeId = targetNode.id
        this.$nextTick(() => {
          if (this.$refs.menuTree) {
            this.$refs.menuTree.setCurrentKey(targetNode.id)
          }
        })

        await this.fetchChildMenus(targetNode.id)
      } catch (error) {
        console.error('Failed to fetch menu tree:', error)
        this.treeData = []
        this.rawTreeData = []
        this.flatMenuList = []
        this.childMenus = []
      } finally {
        this.treeLoading = false
      }
    },
    async fetchChildMenus(parentId) {
      this.childLoading = true
      this.currentPage = 1

      try {
        const flatMenuPayload = await fetchFlatMenuList()
        const childMenus = parentId === ALL_NODE_ID
          ? (flatMenuPayload || []).filter(item => !item.parentId)
          : (flatMenuPayload || []).filter(item => item.parentId === parentId)

        this.childMenus = this.decorateMenus(childMenus)
      } catch (error) {
        console.error('Failed to fetch child menus:', error)
        this.childMenus = []
      } finally {
        this.childLoading = false
      }
    },
    handleNodeClick(node) {
      this.selectedNodeId = node.id
      this.fetchChildMenus(node.id)
    },
    refreshCurrentView() {
      this.initializePage(this.selectedNodeId)
    },
    indexMethod(index) {
      return (this.currentPage - 1) * this.pageSize + index + 1
    },
    handleCurrentChange(page) {
      this.currentPage = page
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
    },
    getDeleteOrder(menuId) {
      const childrenMap = new Map()

      this.flatMenuList.forEach(item => {
        if (item.parentId) {
          if (!childrenMap.has(item.parentId)) {
            childrenMap.set(item.parentId, [])
          }
          childrenMap.get(item.parentId).push(item.id)
        }
      })

      const result = []
      const collect = currentId => {
        const children = childrenMap.get(currentId) || []
        children.forEach(childId => collect(childId))
        result.push(currentId)
      }

      collect(menuId)
      return result
    },
    openEditDialog(row) {
      this.editForm = {
        id: row.id,
        title: row.title,
        path: row.path,
        componentPath: row.componentPath || '',
        sort: Number(row.sort) || 0,
        icon: row.icon || '',
        isRoot: !row.parentId
      }

      this.editDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.editForm) {
          this.$refs.editForm.clearValidate()
        }
      })
    },
    resetEditForm() {
      this.editForm = this.createEditForm()
      if (this.$refs.editForm) {
        this.$refs.editForm.clearValidate()
      }
    },
    async submitEdit() {
      this.$refs.editForm.validate(async valid => {
        if (!valid) {
          return
        }

        this.saving = true
        try {
          const payload = {
            title: this.editForm.title.trim(),
            path: this.editForm.path.trim(),
            sort: this.editForm.sort,
            componentPath: this.editForm.componentPath.trim()
          }

          if (this.editForm.isRoot) {
            payload.icon = this.editForm.icon.trim() || 'el-icon-menu'
          }

          await updateMenu(this.editForm.id, payload)
          this.$toast.success('菜单更新成功')
          this.editDialogVisible = false
          await this.initializePage(this.editForm.id)
          await initDynamicRoutes(true)
          window.dispatchEvent(new Event('menu-updated'))
        } catch (error) {
          console.error('Failed to update menu:', error)
        } finally {
          this.saving = false
        }
      })
    },
    async handleDelete(row) {
      const deleteOrder = this.getDeleteOrder(row.id)
      const suffix = row.childCount > 0
        ? `，这会同时删除 ${deleteOrder.length - 1} 个子节点`
        : ''

      try {
        await this.$confirm(`确定删除菜单“${row.title}”吗${suffix}？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        for (const menuId of deleteOrder) {
          await deleteMenu(menuId)
        }

        this.$toast.success('菜单删除成功')
        await this.initializePage(this.selectedNodeId === row.id ? '' : this.selectedNodeId)
        await initDynamicRoutes(true)
        window.dispatchEvent(new Event('menu-updated'))
      } catch (error) {
        if (error === 'cancel') {
          return
        }

        console.error('Failed to delete menu:', error)
      }
    }
  }
}
</script>

<style scoped>
.menu-data-list-container {
  /* 撑满内容区，滚动下沉到模块内部 */
  height: 100%;
  box-sizing: border-box;
  overflow: auto;
  padding: 0;
}

.menu-data-card {
  min-height: 680px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.menu-data-layout {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 20px;
  min-height: 580px;
}

.menu-tree-panel {
  border-right: 1px solid rgba(31, 45, 61, 0.06);
  padding-right: 20px;
}

.menu-detail-panel {
  min-width: 0;
}

.panel-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
}

.tree-search,
.child-search {
  margin-bottom: 16px;
}

.menu-tree {
  max-height: 560px;
  overflow: auto;
}

.menu-tree ::v-deep .el-tree-node__content {
  height: auto;
  min-height: 34px;
  align-items: flex-start;
  padding: 6px 0;
}

.menu-tree ::v-deep .el-tree-node__expand-icon {
  margin-top: 8px;
}

.menu-tree ::v-deep .el-tree-node__label {
  flex: 1;
  min-width: 0;
}

.tree-node {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 0;
  min-width: 0;
}

.tree-node__title {
  font-size: 14px;
  color: #303133;
  line-height: 1.2;
}

.tree-node__path {
  font-size: 12px;
  color: #909399;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selected-summary {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  margin-bottom: 16px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 8px;
  background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
}

.summary-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}

.summary-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  font-size: 13px;
  color: #606266;
}

.summary-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.child-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.child-toolbar .panel-title {
  margin-bottom: 0;
}

@media (max-width: 960px) {
  .menu-data-layout {
    grid-template-columns: 1fr;
  }

  .menu-tree-panel {
    border-right: none;
    border-bottom: 1px solid rgba(31, 45, 61, 0.06);
    padding-right: 0;
    padding-bottom: 20px;
  }

  .selected-summary,
  .child-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .summary-actions {
    width: 100%;
  }
}
</style>
